import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import useAsyncAddListContacts from '../../../hooks/useAsyncAddListContacts';
import { DEFAULT_COMMON_FORM_NUMBER } from '../../../sic-lib/defaults';
import { RequestStatus } from '../../../sic-lib/enums/RequestStatus';
import { GenericReducer, genericReducer } from '../../../sic-lib/reducers/genericReducer';
import { buildContactsListAssignment } from '../../../sic-lib/types/Lists';
import { IErrorFormValidation } from '../../../sic-lib/utils/formValidationHelpers';
import ButtonLoading from '../../Shared/ButtonLoading';
import SelectContactsList from '../SelectContactsList/SelectContactsList';

const useStyles = makeStyles()((theme: Theme) => ({
    paper: {
        width: '100%',
    },
    text: {
        marginBottom: theme.spacing(3),
    },
    btnCancel: {
        marginRight: theme.spacing(1),
        color: theme.palette.btnDefault.main,
        '&:hover': {
            backgroundColor: theme.palette.btnDefault.light,
        },
    },
}));

interface DialogState {
    listId: number;
    formError: IErrorFormValidation | null;
}

interface Props {
    isOpen: boolean;
    contactIds: number[];
    onDoneHandler: (result?: boolean) => void;
}

export default function DialogAddGroupContacts({ isOpen, contactIds, onDoneHandler }: Props) {
    const { classes } = useStyles();

    const { requestStatus, asyncAddListContacts } = useAsyncAddListContacts();

    const [dialogState, dialogDispatch] = React.useReducer<GenericReducer<DialogState>>(
        genericReducer,
        {
            listId: DEFAULT_COMMON_FORM_NUMBER,
            formError: null,
        },
    );

    const handleCancel = () => {
        onDoneHandler();
    };

    const handleSubmit = async () => {
        try {
            const assignment = buildContactsListAssignment({
                listId: dialogState.listId,
                contactIds,
            });

            await asyncAddListContacts(assignment);

            onDoneHandler(true);
        } catch (error: any) {
            if (error.isFormError) {
                dialogDispatch({ formError: error as IErrorFormValidation });
            }
        }
    };

    return (
        <Dialog
            maxWidth="xs"
            aria-labelledby="dialog_remove_group_contact"
            open={isOpen}
            PaperProps={{
                className: classes.paper,
            }}
        >
            <DialogTitle id="dialog_remove_group_contact">Add Group Contacts</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body1" className={classes.text}>
                    Select a list to add contacts
                </Typography>
                <SelectContactsList
                    fullWidth
                    required
                    error={Boolean(dialogState.formError?.field === 'listId')}
                    helperText={
                        dialogState.formError?.field === 'listId'
                            ? dialogState.formError.message
                            : ''
                    }
                    value={dialogState.listId || ''}
                    onSelectHandler={dialogDispatch}
                />
            </DialogContent>
            <DialogActions>
                <Button className={classes.btnCancel} onClick={handleCancel}>
                    Cancel
                </Button>
                <ButtonLoading
                    isLoading={requestStatus === RequestStatus.active}
                    onClick={handleSubmit}
                    color="secondary"
                    variant="outlined"
                >
                    Save
                </ButtonLoading>
            </DialogActions>
        </Dialog>
    );
}
